import React from "react";
import { NavLink } from "react-router-dom";
import { Milk, Store, ClipboardList, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useT } from "@/i18n/I18nContext";

const NAV = [
  { to: "/portal", labelKey: "client.shop", icon: Store, end: true, testid: "portal-nav-shop" },
  { to: "/portal/orders", labelKey: "client.myOrders", icon: ClipboardList, testid: "portal-nav-orders" },
];

const initials = (name = "") =>
  name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase() || "C";

export const ClientLayout = ({ children, actions }) => {
  const { user, signOut } = useAuth();
  const { t } = useT();
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b border-border bg-card/80 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-[1100px] items-center gap-4 px-4 sm:px-6">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[hsl(var(--brand))] text-white shadow-sm">
              <Milk className="h-5 w-5" />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold tracking-tight">{t("client.store")}</div>
              <div className="text-[11px] text-muted-foreground">{t("client.portal")}</div>
            </div>
          </div>
          <nav className="ml-4 hidden items-center gap-1 sm:flex">
            {NAV.map(({ to, labelKey, icon: Icon, end, testid }) => (
              <NavLink key={to} to={to} end={end} data-testid={testid}
                className={({ isActive }) =>
                  `flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    isActive ? "bg-[hsl(var(--brand)/0.10)] text-[hsl(var(--brand))]" : "text-slate-600 hover:bg-secondary"
                  }`}>
                <Icon className="h-[18px] w-[18px]" /> {t(labelKey)}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            {actions}
            <LanguageSwitcher />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 pl-1.5 pr-2" data-testid="portal-user-menu">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[hsl(var(--brand))] text-xs font-semibold text-white">{initials(user?.name)}</span>
                  <span className="hidden text-left sm:block">
                    <span className="block text-sm font-medium leading-none">{user?.name}</span>
                    <span className="block text-[11px] text-muted-foreground">{t("client.clientRole")}</span>
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>
                  <div className="text-sm font-medium">{user?.name}</div>
                  <div className="text-xs text-muted-foreground">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut} data-testid="portal-logout" className="text-[hsl(var(--danger))]">
                  <LogOut className="mr-2 h-4 w-4" /> {t("userMenu.signOut")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <nav className="flex items-center gap-1 border-t border-border px-4 py-1.5 sm:hidden">
          {NAV.map(({ to, labelKey, icon: Icon, end, testid }) => (
            <NavLink key={to} to={to} end={end} data-testid={`${testid}-m`}
              className={({ isActive }) =>
                `flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium ${
                  isActive ? "bg-[hsl(var(--brand)/0.10)] text-[hsl(var(--brand))]" : "text-slate-600"
                }`}>
              <Icon className="h-4 w-4" /> {t(labelKey)}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="mx-auto w-full max-w-[1100px] px-4 py-6 sm:px-6">{children}</main>
    </div>
  );
};
